import { useFuse } from "apps/visits/core/hooks/use-fuse";
import { getAllUrlParams } from "apps/visits/core/utils/getAllUrlParams";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import { normalizeForSearch } from "@/core/shared/v2/common/helpers/string";
import type { BloodTest } from "../models/blood-test";
import type { BloodTestPackage } from "../models/blood-test-package";
import { useSearchState } from "../store/use-search-state";
import { viewSearchResultsEvent } from "../tracking/events";

type SearchItem = { uuid: string; name: string; searchText: string };

const fuseOptions = {
  keys: [
    { name: "name", weight: 0.5, getFn: (item: SearchItem) => normalizeForSearch(item.name) },
    { name: "searchText", weight: 0.1, getFn: (item: SearchItem) => normalizeForSearch(item.searchText) },
  ],
  threshold: 0.15,
  distance: 75,
  minMatchCharLength: 3,
  ignoreLocation: true,
  useExtendedSearch: false,
  ignoreFieldNorm: true,
  shouldSort: false,
  includeMatches: true,
};

const SEARCH_LIMIT_ITEMS = 100;

type UseCatalogSearchProps = {
  products: BloodTest[] | BloodTestPackage[];
};

export const useCatalogSearch = ({ products }: UseCatalogSearchProps) => {
  const { searchTerm, setSearchTerm, setSearchResults, clearSearch, setIsSearching } = useSearchState();
  const queryParams = useMemo(() => getAllUrlParams(), []);
  const { search: searchFuse } = useFuse<SearchItem>({
    data: products?.map(mapToSearchable) || [],
    options: fuseOptions,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: products are used inside the debounce wrapper
  const searchDebounced = useCallback(
    debounce((term: string) => {
      if (term.length < 2) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }
      const normalizedTerm = normalizeForSearch(term);
      const searchResults = searchFuse(normalizedTerm, { limit: SEARCH_LIMIT_ITEMS });

      const productsResult = searchResults.map((searchResult) => findProductByUuid(searchResult.uuid, products));
      setSearchResults(productsResult);
      setIsSearching(false);

      viewSearchResultsEvent({
        searchTerm: term,
        findResults: productsResult.length > 0,
        numberOfSearches: productsResult.length,
        firstResult: productsResult[0]?.name,
        secondResult: productsResult[1]?.name,
        thirdResult: productsResult[2]?.name,
      });
    }, 700),
    [products],
  );

  const search = useCallback(
    (term: string) => {
      setSearchTerm(term);
      setIsSearching(true);
      searchDebounced(term);
    },
    [setSearchTerm, setIsSearching, searchDebounced],
  );

  useEffect(() => {
    const { product: productQueryParam } = queryParams;
    if (products && products.length > 0 && searchTerm === productQueryParam) {
      search(productQueryParam);
    }
  }, [queryParams, products, search, searchTerm]);

  const clearSearchResults = () => {
    clearSearch();
  };

  return { search, clearSearchResults };
};

function mapToSearchable(product: BloodTest | BloodTestPackage) {
  return {
    uuid: product.uuid,
    name: product.name,
    searchText: `${product.description} ${
      "bloodTests" in product ? product.bloodTests.map((bloodTest) => bloodTest.name).join(" ") : ""
    }`,
  };
}

function findProductByUuid(uuid: string, products: BloodTest[] | BloodTestPackage[]) {
  return products.find((product) => product.uuid === uuid);
}
